
import { supabase } from '@/integrations/supabase/client';
import DOMPurify from 'dompurify';

export interface SecurityConfig {
  passwordMinLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxLoginAttempts: number;
  lockoutDuration: number; // in minutes
}

export interface LoginAttempt {
  email: string;
  timestamp: number;
  success: boolean;
  ip?: string;
}

class SecurityService {
  private loginAttempts: Map<string, LoginAttempt[]> = new Map();
  private securityConfig: SecurityConfig = {
    passwordMinLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    maxLoginAttempts: 5,
    lockoutDuration: 15
  };

  // Password validation
  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < this.securityConfig.passwordMinLength) {
      errors.push(`Password must be at least ${this.securityConfig.passwordMinLength} characters long`);
    }

    if (this.securityConfig.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (this.securityConfig.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (this.securityConfig.requireNumbers && !/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (this.securityConfig.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Rate limiting for login attempts
  canAttemptLogin(email: string): { allowed: boolean; remainingAttempts?: number; lockoutUntil?: Date } {
    const attempts = this.loginAttempts.get(email) || [];
    const now = Date.now();
    const lockoutMs = this.securityConfig.lockoutDuration * 60 * 1000;

    // Clean old attempts (older than lockout duration)
    const recentAttempts = attempts.filter(attempt => 
      now - attempt.timestamp < lockoutMs
    );

    // Update the map with cleaned attempts
    this.loginAttempts.set(email, recentAttempts);

    // Count failed attempts in the lockout window
    const failedAttempts = recentAttempts.filter(attempt => !attempt.success);

    if (failedAttempts.length >= this.securityConfig.maxLoginAttempts) {
      const oldestFailedAttempt = failedAttempts[0];
      const lockoutUntil = new Date(oldestFailedAttempt.timestamp + lockoutMs);
      
      if (now < lockoutUntil.getTime()) {
        return {
          allowed: false,
          lockoutUntil
        };
      }
    }

    return {
      allowed: true,
      remainingAttempts: Math.max(0, this.securityConfig.maxLoginAttempts - failedAttempts.length)
    };
  }

  // Record login attempt
  recordLoginAttempt(email: string, success: boolean, ip?: string): void {
    const attempts = this.loginAttempts.get(email) || [];
    attempts.push({
      email,
      timestamp: Date.now(),
      success,
      ip
    });

    // Keep only recent attempts to prevent memory bloat
    const cutoff = Date.now() - (24 * 60 * 60 * 1000); // 24 hours
    const recentAttempts = attempts.filter(attempt => attempt.timestamp > cutoff);
    
    this.loginAttempts.set(email, recentAttempts);
  }

  // Content sanitization
  sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: []
    });
  }

  sanitizeText(text: string): string {
    return text.replace(/[<>]/g, '');
  }

  // Security event logging - gracefully handle if function doesn't exist
  async logSecurityEvent(eventType: string, userId?: string, details?: any): Promise<void> {
    try {
      // Try to log to the security audit function
      const { error } = await supabase.rpc('log_security_event', {
        p_event_type: eventType,
        p_user_id: userId,
        p_event_details: details || {},
        p_ip_address: await this.getUserIP(),
        p_user_agent: navigator.userAgent
      });

      if (error) {
        // If the RPC call fails, log to console as fallback
        console.log('Security Event (logged to console):', {
          event_type: eventType,
          user_id: userId,
          event_details: details || {},
          ip_address: await this.getUserIP(),
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      // Graceful fallback - log to console if anything fails
      console.log('Security Event (fallback logging):', {
        event_type: eventType,
        user_id: userId,
        event_details: details || {},
        timestamp: new Date().toISOString(),
        error: error
      });
    }
  }

  // Get user IP (in production, this would come from server-side)
  private async getUserIP(): Promise<string> {
    try {
      // In production, get this from your backend
      return 'client-side'; // Placeholder
    } catch {
      return 'unknown';
    }
  }

  // Validate user permissions for sensitive operations
  async validateUserPermission(requiredRole: string, operation: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Use the safe role checking function
      const { data: hasRole, error } = await supabase.rpc('has_role_safe', {
        user_id_param: user.id,
        role_param: requiredRole
      });

      if (error) {
        await this.logSecurityEvent('permission_check_failed', user.id, {
          required_role: requiredRole,
          operation,
          error: error.message
        });
        return false;
      }

      if (!hasRole) {
        await this.logSecurityEvent('unauthorized_access_attempt', user.id, {
          required_role: requiredRole,
          operation
        });
      }

      return hasRole;
    } catch (error) {
      console.error('Permission validation error:', error);
      return false;
    }
  }
}

export const securityService = new SecurityService();
